import DashboardLayout from "@/components/dashboard/DashboardLayout";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/hooks/use-auth";
import { Link } from "wouter";
import { 
  Users, 
  Building, 
  BriefcaseIcon, 
  BarChart, 
  Newspaper, 
  Settings,
  ArrowUpRight,
  ArrowDownRight,
  UserPlus,
  Mail
} from "lucide-react";

export default function AdminDashboard() {
  const { user } = useAuth();

  return (
    <DashboardLayout pageTitle="Admin Dashboard">
      {/* Overview Cards */}
      <section className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-slate-500">Total Users</p>
                <h3 className="text-2xl font-bold mt-1">1,247</h3>
              </div>
              <div className="p-3 bg-primary-100 rounded-full">
                <Users className="h-6 w-6 text-primary-600" />
              </div>
            </div>
            <div className="flex items-center mt-4">
              <ArrowUpRight className="text-green-500 h-4 w-4 mr-1" />
              <span className="text-xs text-green-600">+12% this month</span>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-slate-500">Active Jobs</p>
                <h3 className="text-2xl font-bold mt-1">86</h3>
              </div>
              <div className="p-3 bg-primary-100 rounded-full">
                <BriefcaseIcon className="h-6 w-6 text-primary-600" />
              </div>
            </div>
            <div className="flex items-center mt-4">
              <ArrowUpRight className="text-green-500 h-4 w-4 mr-1" />
              <span className="text-xs text-green-600">+5% this month</span>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-slate-500">Employer Partners</p>
                <h3 className="text-2xl font-bold mt-1">42</h3>
              </div>
              <div className="p-3 bg-primary-100 rounded-full">
                <Building className="h-6 w-6 text-primary-600" />
              </div>
            </div>
            <div className="flex items-center mt-4">
              <ArrowUpRight className="text-green-500 h-4 w-4 mr-1" />
              <span className="text-xs text-green-600">+3 this month</span>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-slate-500">Placements YTD</p>
                <h3 className="text-2xl font-bold mt-1">368</h3>
              </div>
              <div className="p-3 bg-primary-100 rounded-full">
                <BarChart className="h-6 w-6 text-primary-600" />
              </div>
            </div>
            <div className="flex items-center mt-4">
              <ArrowDownRight className="text-red-500 h-4 w-4 mr-1" />
              <span className="text-xs text-red-600">-2% vs target</span>
            </div>
          </CardContent>
        </Card>
      </section>

      {/* Quick Actions */}
      <section className="mb-8">
        <Card>
          <CardHeader>
            <CardTitle>Administrative Actions</CardTitle>
            <CardDescription>Quick access to key management functions</CardDescription>
          </CardHeader>
          <CardContent className="flex flex-wrap gap-3">
            <Button asChild className="flex items-center">
              <Link href="/dashboard/admin/users/new">
                <UserPlus className="mr-2 h-4 w-4" />
                Add Case Worker
              </Link>
            </Button>
            <Button asChild variant="outline">
              <Link href="/dashboard/admin/jobs">
                Manage Job Listings
              </Link>
            </Button>
            <Button asChild variant="outline">
              <Link href="/dashboard/admin/success-stories">
                Manage Success Stories
              </Link>
            </Button>
            <Button asChild variant="outline">
              <Link href="/dashboard/admin/settings">
                System Settings
              </Link>
            </Button>
          </CardContent>
        </Card>
      </section>

      {/* Performance Overview */}
      <section className="mb-8">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-semibold">Performance Overview</h2>
          <Button asChild variant="outline" size="sm">
            <Link href="/dashboard/admin/reports">
              <BarChart className="mr-2 h-4 w-4" />
              Full Reports
            </Link>
          </Button>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-base">Job Placements</CardTitle>
              <CardDescription>This year vs. last year</CardDescription>
            </CardHeader>
            <CardContent className="h-64 flex items-center justify-center bg-slate-50">
              <p className="text-slate-400 text-sm">Chart Placeholder</p>
            </CardContent>
          </Card>
          
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-base">User Growth</CardTitle>
              <CardDescription>By user type over time</CardDescription>
            </CardHeader>
            <CardContent className="h-64 flex items-center justify-center bg-slate-50">
              <p className="text-slate-400 text-sm">Chart Placeholder</p>
            </CardContent>
          </Card>
          
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-base">Employer Retention</CardTitle>
              <CardDescription>Partner activity over time</CardDescription>
            </CardHeader>
            <CardContent className="h-64 flex items-center justify-center bg-slate-50">
              <p className="text-slate-400 text-sm">Chart Placeholder</p>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Two-column layout for Recent Users and Contact Submissions */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">
        <section>
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-xl font-semibold">Recent Users</h2>
            <Button asChild variant="outline" size="sm">
              <Link href="/dashboard/admin/users">View All</Link>
            </Button>
          </div>
          <div className="space-y-4">
            {[
              { name: "James Wilson", role: "Client", date: "Apr 15, 2025", status: "Active" },
              { name: "Sarah Thompson", role: "Case Worker", date: "Apr 14, 2025", status: "Active" },
              { name: "Metro Logistics", role: "Employer", date: "Apr 13, 2025", status: "Active" },
              { name: "Maria Garcia", role: "Client", date: "Apr 12, 2025", status: "Inactive" },
              { name: "Robert Johnson", role: "Client", date: "Apr 11, 2025", status: "Active" }
            ].map((user, index) => (
              <Card key={index}>
                <CardContent className="p-4">
                  <div className="flex justify-between">
                    <div className="flex items-center">
                      <div className="w-10 h-10 rounded-full bg-primary-100 text-primary-600 flex items-center justify-center mr-3">
                        {user.name.split(' ').map(n => n[0]).join('').toUpperCase()}
                      </div>
                      <div>
                        <p className="font-medium">{user.name}</p>
                        <div className="flex items-center text-sm">
                          <span className={`inline-block w-2 h-2 rounded-full mr-2 ${
                            user.status === "Active" ? "bg-green-500" : "bg-slate-400"
                          }`}></span>
                          <span className="text-slate-500">{user.role}</span>
                        </div>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="text-xs text-slate-500">{user.date}</p>
                      <Button size="sm" variant="ghost" className="mt-1 h-7">View</Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </section>

        <section>
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-xl font-semibold">Recent Contact Submissions</h2>
            <Button asChild variant="outline" size="sm">
              <Link href="/dashboard/admin/contact">View All</Link>
            </Button>
          </div>
          <div className="space-y-4">
            {[
              { name: "John Smith", subject: "Volunteer Inquiry", date: "Apr 15, 2025", status: "New" },
              { name: "Jane Doe", subject: "Program Question", date: "Apr 14, 2025", status: "Replied" },
              { name: "Michael Brown", subject: "Donation Question", date: "Apr 13, 2025", status: "New" },
              { name: "Lisa Anderson", subject: "Partnership Proposal", date: "Apr 12, 2025", status: "Replied" },
              { name: "Kevin Martin", subject: "Employment Question", date: "Apr 11, 2025", status: "New" }
            ].map((submission, index) => (
              <Card key={index}>
                <CardContent className="p-4">
                  <div className="flex justify-between">
                    <div className="flex items-center">
                      <div className="w-10 h-10 rounded-full bg-slate-100 text-slate-600 flex items-center justify-center mr-3">
                        <Mail className="h-5 w-5" />
                      </div>
                      <div>
                        <p className="font-medium">{submission.name}</p>
                        <p className="text-sm text-slate-500">{submission.subject}</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="text-xs text-slate-500">{submission.date}</p>
                      <span className={`inline-block text-xs px-2 py-1 rounded-full mt-1 ${
                        submission.status === "New" ? "bg-blue-100 text-blue-700" : "bg-green-100 text-green-700"
                      }`}>
                        {submission.status}
                      </span>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </section>
      </div>
      
      {/* Success Stories */}
      <section>
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-semibold">Recent Success Stories</h2>
          <Button asChild variant="outline" size="sm">
            <Link href="/dashboard/admin/success-stories">
              <Newspaper className="mr-2 h-4 w-4" />
              Manage Stories
            </Link>
          </Button>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {[
            { client: "James Wilson", title: "From Incarceration to IT Career", status: "Published", date: "Apr 10, 2025" },
            { client: "Maria Garcia", title: "Finding Stability After Addiction", status: "Draft", date: "Apr 8, 2025" },
            { client: "Robert Johnson", title: "A New Start with Metro Logistics", status: "Published", date: "Apr 5, 2025" }
          ].map((story, index) => (
            <Card key={index}>
              <div className="h-40 bg-slate-200"></div>
              <CardContent className="p-4">
                <div className="flex justify-between mb-2">
                  <h3 className="font-medium truncate">{story.title}</h3>
                  <span className={`text-xs px-2 py-1 rounded-full ${
                    story.status === "Published" ? "bg-green-100 text-green-700" : "bg-amber-100 text-amber-700"
                  }`}>
                    {story.status}
                  </span>
                </div>
                <p className="text-sm text-slate-500">{story.client}</p>
                <div className="flex justify-between items-center mt-4">
                  <span className="text-xs text-slate-500">{story.date}</span>
                  <Button size="sm" variant="outline">Edit</Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </section>
    </DashboardLayout>
  );
}